import clsx from 'clsx';
import { useHorizontalScroll } from '../../../hooks/useHorizontalScroll';
import ChevronButton from '../../Buttons/ChevronButton';
import BoxImg from '../../Cards/BoxImg';
import { ODS } from '../../../utils/const';

const { REACT_APP_BACKOFFICE_CONNECTION, REACT_APP_BACKOFFICE_FILE_UPLOAD_PATH } = process.env;

export interface SelectableItem {
    id: number;
    nombre: string;
    descripcion: string;
    imagen: string;
    color: string;
}

interface ModuleConfig {
    itemsThreshold: number;
    itemsMobileThreshold: number;
    cardHeight: string;
    imgWidth: string;
    imgHeight: string;
    containerWidth: string;
    scrollAmount: number;
}

const MODULE_CONFIG: Record<string, ModuleConfig> = {
    ods: {
        itemsThreshold: 6,
        itemsMobileThreshold: 3,
        cardHeight: 'h-[9.375rem]',
        imgWidth: 'w-[7.375rem]',
        imgHeight: 'h-[7.375rem]',
        containerWidth: 'w-[9.375rem]',
        scrollAmount: 155,
    },
    'grupos-prioritarios': {
        itemsThreshold: 5,
        itemsMobileThreshold: 3,
        cardHeight: 'h-[9.375rem] md:h-[8.75rem]',
        imgWidth: 'w-[8rem]',
        imgHeight: 'h-[7rem]',
        containerWidth: '!w-[9.9375rem] md:!w-[9.7875rem] lg:!w-[10.075rem] xl:!w-[13.175rem]',
        scrollAmount: 175,
    },
};

const DEFAULT_CONFIG = MODULE_CONFIG['ods'];

interface SelectableCardListProps {
    items: SelectableItem[];
    selectedId?: number;
    onSelect: (id: number) => void;
    isMobile: boolean;
    module: string;
}

function SelectableCardList({
    items,
    selectedId,
    onSelect,
    isMobile,
    module,
}: SelectableCardListProps) {
    const config = MODULE_CONFIG[module] ?? DEFAULT_CONFIG;
    const { itemsThreshold, itemsMobileThreshold, cardHeight, imgWidth, imgHeight, containerWidth, scrollAmount } = config;

    const selected = items.find((el) => el.id === selectedId);
    const selectedIndex = selectedId ? items.findIndex((el) => el.id === selectedId) : undefined;

    const {
        containerRef,
        hasOverflowLeft,
        hasOverflowRight,
        scroll,
        checkOverflow,
    } = useHorizontalScroll([items], {
        selectedIndex,
        scrollAmount: scrollAmount,
    });

    const showButtons =
        (items.length > itemsThreshold || isMobile) &&
        !(items.length < itemsMobileThreshold && isMobile);

    return (
        <div className="flex flex-col mt-8 w-full" id='ods/groups'>
            <div className='flex items-center'>
                {showButtons && (
                    <ChevronButton
                        onClick={() => scroll("left")}
                        direction="left"
                        disabled={!hasOverflowLeft}
                        aria_label="Scroll left"
                        buttonStyles={`bg-brand-green-5 shadow-custom-dual rounded-xsm h-[2.5rem] w-[2.5rem] ${!hasOverflowLeft ? 'opacity-60' : ''}`}
                    />
                )}
                <div
                    ref={containerRef}
                    className={clsx(
                        'flex gap-4 overflow-x-auto overflow-y-visible scrollbar-hide flex-grow relative pb-4',
                        { 'mx-2': showButtons }
                    )}
                    onScroll={checkOverflow}
                >
                    {items.map((item) => (
                        <button
                            key={item.id}
                            onClick={() => onSelect(item.id)}
                            style={{ borderColor: item.id === selectedId ? item.color : undefined }}
                            className={clsx(
                                'transition-all duration-300 relative overflow-visible rounded-sm border',
                                cardHeight,
                                item.id === selectedId
                                    ? 'shadow-custom-dual'
                                    : 'border-system-light-20 hover:brightness-105 hover:shadow-custom-dual'
                            )}
                        >
                            <BoxImg
                                icono={`${REACT_APP_BACKOFFICE_CONNECTION}${REACT_APP_BACKOFFICE_FILE_UPLOAD_PATH}${item.imagen.replace("files/", "")}`}
                                alt={item.nombre.toLowerCase()}
                                color={item.id === selectedId ? item.color : ''}
                                classImg={clsx(`${imgWidth} ${imgHeight}`, {
                                    'filter [filter:brightness(0)_invert(1)]': module === ODS && item.id === selectedId,
                                })}
                                className={containerWidth}
                                variant='ods-gp'
                            />
                            {item.id === selectedId && (
                                <p
                                    className="shape-polygon w-[2rem] h-[1.5rem] absolute z-[456] bottom-[-1.5rem]"
                                    style={{ background: item.color }}
                                />
                            )}
                        </button>
                    ))}
                </div>
                {showButtons && (
                    <ChevronButton
                        onClick={() => scroll("right")}
                        direction="right"
                        disabled={!hasOverflowRight}
                        aria_label="Scroll right"
                        buttonStyles={`bg-brand-green-5 shadow-custom-dual rounded-xsm h-[2.5rem] w-[2.5rem] ${!hasOverflowRight ? 'opacity-60' : ''}`}
                    />
                )}
            </div>
            {selected && (
                <div className="mt-4 w-full">
                    <div
                        className="bg-primary-100 p-4 rounded-sm shadow-custom-dual"
                        style={{ borderTop: `8px ${module === ODS ? selected.color : '#A8444B'} solid` }}
                    >
                        <h4
                            className="text-tmedium md:text-hsmall mb-3"
                            style={{ color: module === ODS ? selected.color : '#A8444B' }}
                        >
                            {selected.nombre}
                        </h4>
                        <p className="text-bmedium md:text-blarge text-primary-10">{selected.descripcion}</p>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SelectableCardList;